import { AiHealthBanner } from "@/app/ai-health-banner";
import { mobileTabs, navigation, settingsNavigation, visibleItems } from "@/app/navigation";
import { SettingsFlyout } from "@/app/settings-flyout";
import {
  readSidebarCollapsed,
  saveSidebarCollapsed,
  useDesktopSidebar,
} from "@/app/sidebar-collapse";
import { WorkspaceProvider } from "@/app/workspace-context";
import { AppointmentReminders } from "@/features/appointments/appointment-reminders";
import { CallRecorderBar } from "@/features/calls/call-recorder-context";
import { AppSettings, PwaUpdateBanner } from "@/features/pwa/app-settings";
import { revokePushBeforeSignOut } from "@/features/pwa/push-device";
import { ApiError, api, readableApiError } from "@/shared/api";
import { connectionStatusLabel, initials } from "@/shared/format";
import { queryKeys } from "@/shared/query";
import { Alert, Badge, IconButton, LoadingBlock, cx } from "@/shared/ui/primitives";
import { useToast } from "@/shared/ui/toast";
import { useWorkspaceEvents } from "@/shared/use-workspace-events";
import { canManageWorkspace, safeTimeZone } from "@repo/polaris-shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  Outlet,
  useMatchRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import {
  ChevronDown,
  CircleUserRound,
  Compass,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

/** The fields that open the phone's keyboard: not checkboxes, dates or buttons. */
const KEYBOARD_FIELDS = [
  "textarea",
  "[contenteditable='true']",
  "input:not([type])",
  ...["text", "search", "email", "tel", "url", "number", "password"].map(
    (type) => `input[type='${type}']`,
  ),
].join(", ");

export function WorkspaceShell() {
  const { workspaceSlug } = useParams({ from: "/w/$workspaceSlug" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  // Desktop only: on the phone the sidebar is the side menu, always whole.
  const [collapsedChoice, setCollapsedChoice] = useState(readSidebarCollapsed);
  const collapsed = useDesktopSidebar() && collapsedChoice;
  const toggleCollapsed = () => {
    setCollapsedChoice(!collapsedChoice);
    saveSidebarCollapsed(!collapsedChoice);
  };
  useEffect(() => {
    const viewport = window.visualViewport;
    const updateHeight = (event?: Event) => {
      // The keyboard shrinks the visual viewport on iOS; pinch zoom must stay independent.
      if (viewport && viewport.scale !== 1) return;
      const height = viewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty("--app-viewport-height", `${height}px`);
      // Where the visible part starts: iOS scrolls it over the page to show the field.
      document.documentElement.style.setProperty(
        "--app-viewport-top",
        `${viewport?.offsetTop ?? 0}px`,
      );
      const target =
        event instanceof FocusEvent && event.type === "focusout"
          ? event.relatedTarget
          : event?.type === "click"
            ? event.target
            : document.activeElement;
      // The composer, and any other field the keyboard writes in (a proposal, a
      // follow-up, the search): the bars give way to what is being written.
      const writesIn = (element: EventTarget | null) =>
        element instanceof Element &&
        Boolean(element.closest(".composer") || element.matches(KEYBOARD_FIELDS));
      // Hiding the bars moves the field from under the finger, so the click that
      // follows lands next to it: what still has the focus counts too.
      const writing =
        writesIn(target) || (event?.type === "click" && writesIn(document.activeElement));
      // Leave space stable between pointer-down focus changes and the actual click.
      if (
        event instanceof FocusEvent &&
        !writing &&
        document.documentElement.hasAttribute("data-compact-composer")
      )
        return;
      document.documentElement.toggleAttribute(
        "data-compact-composer",
        Boolean(writing && (height < 480 || window.innerHeight - height > 120)),
      );
    };
    updateHeight();
    viewport?.addEventListener("resize", updateHeight, { passive: true });
    viewport?.addEventListener("scroll", updateHeight, { passive: true });
    window.addEventListener("resize", updateHeight, { passive: true });
    document.addEventListener("focusin", updateHeight);
    document.addEventListener("focusout", updateHeight);
    document.addEventListener("click", updateHeight);
    const updateAfterTab = (event: KeyboardEvent) => {
      if (event.key === "Tab" || event.key === "Escape") updateHeight();
    };
    document.addEventListener("keyup", updateAfterTab);
    return () => {
      viewport?.removeEventListener("resize", updateHeight);
      viewport?.removeEventListener("scroll", updateHeight);
      window.removeEventListener("resize", updateHeight);
      document.removeEventListener("focusin", updateHeight);
      document.removeEventListener("focusout", updateHeight);
      document.removeEventListener("click", updateHeight);
      document.removeEventListener("keyup", updateAfterTab);
      document.documentElement.style.removeProperty("--app-viewport-height");
      document.documentElement.style.removeProperty("--app-viewport-top");
      document.documentElement.removeAttribute("data-compact-composer");
    };
  }, []);
  useEffect(() => {
    if (!menuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (document.querySelector("dialog[open]")) return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);
  const matchRoute = useMatchRoute();
  // "Impostazioni" opens by itself on its own pages; it can still be closed.
  const settingsActive = settingsNavigation.some((item) =>
    Boolean(matchRoute({ to: item.to, fuzzy: true })),
  );
  const [settingsOpen, setSettingsOpen] = useState(settingsActive);
  useEffect(() => {
    if (settingsActive) setSettingsOpen(true);
  }, [settingsActive]);
  // The inbox and the approvals scroll inside their panes: the page must be
  // exactly as tall as the screen, banners included.
  const fillScreen = Boolean(
    matchRoute({ to: "/w/$workspaceSlug/inbox", fuzzy: true }) ||
      matchRoute({ to: "/w/$workspaceSlug/approvals", fuzzy: true }) ||
      matchRoute({ to: "/w/$workspaceSlug/wiki", fuzzy: true }),
  );
  // One chat open (the inbox's, or the one over "Approvazioni"): on the phone it
  // takes the whole screen, without the Polaris bar and the tabs (owner, 2026-10-21).
  const { lead: approvalsLead } = useSearch({ strict: false }) as { lead?: string };
  const chatOpen = Boolean(
    matchRoute({ to: "/w/$workspaceSlug/inbox/$leadId" }) ||
      (approvalsLead && matchRoute({ to: "/w/$workspaceSlug/approvals" })),
  );
  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: api.auth.session,
    retry: false,
  });
  const workspace = useQuery({
    queryKey: queryKeys.workspace(workspaceSlug),
    queryFn: () => api.workspace.bootstrap(workspaceSlug),
    retry: (attempt, error) => !(error instanceof ApiError && error.status < 500) && attempt < 2,
  });
  const liveState = useWorkspaceEvents(workspaceSlug, queryClient);

  if (workspace.isPending) {
    return (
      <main className="entry-screen" id="main-content">
        <div className="brand-lockup brand-lockup--centered">
          <span className="brand-mark">
            <Compass aria-hidden="true" />
          </span>
          <strong>Polaris Leads</strong>
        </div>
        <LoadingBlock lines={5} label="Caricamento dello spazio di lavoro" />
      </main>
    );
  }
  if (workspace.error instanceof ApiError && workspace.error.status === 401)
    return <Navigate to="/login" replace />;
  if (workspace.isError) {
    return (
      <main className="entry-screen" id="main-content">
        <Alert
          title="Non riusciamo ad aprire lo spazio di lavoro"
          action={
            <button type="button" className="text-button" onClick={() => void workspace.refetch()}>
              Riprova
            </button>
          }
        >
          {readableApiError(workspace.error, "Aggiorna la pagina o riprova tra poco.")}
        </Alert>
      </main>
    );
  }

  const data = workspace.data;
  const manage = canManageWorkspace(data.workspace.role);
  const reviewMode = data.workspace.isDemo || data.workspace.role === "reviewer";
  const primaryConnection = data.connections.find((connection) => connection.kind === "whatsapp");
  const visibleNavigation = visibleItems(navigation, manage);
  const visibleSettings = visibleItems(settingsNavigation, manage);
  const signOut = async () => {
    try {
      await revokePushBeforeSignOut(data.session.user.id);
      await api.auth.signOut();
      queryClient.clear();
      await navigate({ to: "/login", replace: true });
    } catch (error) {
      pushToast({
        tone: "error",
        title: "Disconnessione non riuscita",
        description: readableApiError(error, "Riprova."),
      });
    }
  };

  return (
    <WorkspaceProvider value={data}>
      <div
        className={cx(
          "workspace-shell",
          collapsed && "workspace-shell--collapsed",
          chatOpen && "workspace-shell--chat",
        )}
      >
        <header className="mobile-header">
          <button
            type="button"
            ref={menuButton}
            className="icon-button"
            aria-label="Apri navigazione"
            aria-expanded={menuOpen}
            aria-controls="workspace-navigation"
            onClick={() => setMenuOpen(true)}
          >
            <Menu aria-hidden="true" />
          </button>
          <Link to="/w/$workspaceSlug/inbox" params={{ workspaceSlug }} className="brand-lockup">
            <span className="brand-mark">
              <Compass aria-hidden="true" />
            </span>
            <strong>Polaris Leads</strong>
          </Link>
          <span
            className={`live-dot live-dot--${liveState}`}
            aria-label={
              liveState === "live"
                ? "Aggiornamenti in tempo reale attivi"
                : "Aggiornamenti in riconnessione"
            }
          />
        </header>
        <button
          className={menuOpen ? "nav-scrim nav-scrim--open" : "nav-scrim"}
          type="button"
          aria-label="Chiudi navigazione"
          onClick={() => setMenuOpen(false)}
        />
        <aside
          id="workspace-navigation"
          className={menuOpen ? "workspace-nav workspace-nav--open" : "workspace-nav"}
          aria-label="Navigazione principale"
        >
          <div className="workspace-nav__top">
            <Link to="/w/$workspaceSlug/inbox" params={{ workspaceSlug }} className="brand-lockup">
              <span className="brand-mark">
                <Compass aria-hidden="true" />
              </span>
              <strong>Polaris Leads</strong>
            </Link>
            <IconButton
              className="workspace-nav__close"
              aria-label="Chiudi navigazione"
              onClick={() => setMenuOpen(false)}
            >
              <X aria-hidden="true" />
            </IconButton>
            <IconButton
              className="workspace-nav__collapse"
              aria-label={collapsed ? "Espandi la barra laterale" : "Comprimi la barra laterale"}
              title={collapsed ? "Espandi la barra laterale" : "Comprimi la barra laterale"}
              aria-expanded={!collapsed}
              aria-controls="workspace-navigation"
              onClick={toggleCollapsed}
            >
              {collapsed ? (
                <PanelLeftOpen aria-hidden="true" size={18} />
              ) : (
                <PanelLeftClose aria-hidden="true" size={18} />
              )}
            </IconButton>
          </div>
          <div
            className="workspace-switcher"
            title={collapsed ? `${data.workspace.name}: cambia spazio di lavoro` : undefined}
          >
            <span className="avatar avatar--workspace">{initials(data.workspace.name)}</span>
            <span>
              <strong>{data.workspace.name}</strong>
              <small>{reviewMode ? "Ambiente di revisione" : "Spazio di lavoro"}</small>
            </span>
            <ChevronDown aria-hidden="true" size={16} />
            <select
              className="workspace-switcher__select"
              aria-label="Cambia spazio di lavoro"
              value={workspaceSlug}
              onChange={(event) => {
                setMenuOpen(false);
                void navigate({
                  to: "/w/$workspaceSlug/inbox",
                  params: { workspaceSlug: event.target.value },
                });
              }}
            >
              {(session.data?.workspaces ?? [data.workspace]).map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <nav className="workspace-nav__links">
            {visibleNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  params={{ workspaceSlug }}
                  className={cx(
                    "nav-link",
                    item.activeOn &&
                      matchRoute({ to: item.activeOn, fuzzy: true }) &&
                      "nav-link--active",
                  )}
                  activeProps={{ className: "nav-link nav-link--active" }}
                  title={collapsed ? item.label : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon aria-hidden="true" size={19} />
                  <span className="nav-link__label">{item.label}</span>
                </Link>
              );
            })}
            {visibleSettings.length > 0 && collapsed ? (
              <SettingsFlyout
                items={visibleSettings}
                workspaceSlug={workspaceSlug}
                active={settingsActive}
              />
            ) : visibleSettings.length > 0 ? (
              <div className="nav-group">
                <button
                  type="button"
                  className={cx(
                    "nav-link nav-group__toggle",
                    settingsActive && !settingsOpen && "nav-link--active",
                  )}
                  aria-expanded={settingsOpen}
                  aria-controls="workspace-settings-navigation"
                  onClick={() => setSettingsOpen(!settingsOpen)}
                >
                  <Settings aria-hidden="true" size={19} />
                  <span className="nav-link__label">Impostazioni</span>
                  <ChevronDown aria-hidden="true" size={16} className="nav-group__chevron" />
                </button>
                {settingsOpen ? (
                  <div className="nav-group__items" id="workspace-settings-navigation">
                    {visibleSettings.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        params={{ workspaceSlug }}
                        className="nav-link nav-link--child"
                        activeProps={{ className: "nav-link nav-link--child nav-link--active" }}
                        onClick={() => setMenuOpen(false)}
                      >
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>
          <div className="workspace-nav__status">
            <span className="eyebrow">Connessione principale</span>
            {primaryConnection ? (
              <Link
                to="/w/$workspaceSlug/integrations"
                params={{ workspaceSlug }}
                className="connection-summary"
                title={
                  collapsed
                    ? `${primaryConnection.displayPhoneNumber ?? primaryConnection.name}: ${connectionStatusLabel[primaryConnection.status]}`
                    : undefined
                }
              >
                <span className={`status-dot status-dot--${primaryConnection.status}`} />
                <span>
                  <strong>{primaryConnection.displayPhoneNumber ?? primaryConnection.name}</strong>
                  <small>{connectionStatusLabel[primaryConnection.status]}</small>
                </span>
              </Link>
            ) : (
              <Link
                to="/w/$workspaceSlug/onboarding"
                params={{ workspaceSlug }}
                className="connection-summary"
                title={collapsed ? "WhatsApp non collegato: completa la configurazione" : undefined}
              >
                <span className="status-dot status-dot--pending" />
                <span>
                  <strong>WhatsApp non collegato</strong>
                  <small>Completa la configurazione</small>
                </span>
              </Link>
            )}
          </div>
          <AppSettings
            workspaceSlug={workspaceSlug}
            userId={data.session.user.id}
            title={collapsed ? "App e notifiche" : undefined}
          />
          <div className="workspace-nav__account">
            <span
              className="avatar"
              title={
                collapsed ? `${data.session.user.name} · ${data.session.user.email}` : undefined
              }
            >
              <CircleUserRound aria-hidden="true" />
            </span>
            <span>
              <strong>{data.session.user.name}</strong>
              <small>{data.session.user.email}</small>
            </span>
            <IconButton
              aria-label="Esci da Polaris Leads"
              title={collapsed ? "Esci" : undefined}
              onClick={() => void signOut()}
            >
              <LogOut aria-hidden="true" size={18} />
            </IconButton>
          </div>
        </aside>
        <div className={cx("workspace-body", fillScreen && "workspace-body--fill")}>
          {liveState === "offline" ? (
            <output className="connection-banner">
              Aggiornamenti in tempo reale interrotti. I dati si aggiorneranno appena torna la
              connessione.
            </output>
          ) : null}
          {manage && data.config.aiEnabled && !data.workspace.isDemo ? (
            <AiHealthBanner workspaceSlug={workspaceSlug} />
          ) : null}
          <CallRecorderBar />
          <PwaUpdateBanner />
          <AppointmentReminders
            workspaceSlug={workspaceSlug}
            timeZone={safeTimeZone(data.config.timezone)}
          />
          {reviewMode ? (
            <div className="demo-banner">
              <Badge tone="warning">Ambiente di verifica</Badge> I dati sono sintetici e le azioni
              consentite restano limitate agli asset di test collegati.
            </div>
          ) : null}
          <main id="main-content" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
        <nav className="mobile-tabs" aria-label="Navigazione rapida">
          {mobileTabs(visibleNavigation).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.label}
                to={item.to}
                params={{ workspaceSlug }}
                className={cx(
                  "mobile-tab",
                  item.activeOn &&
                    matchRoute({ to: item.activeOn, fuzzy: true }) &&
                    "mobile-tab--active",
                )}
                activeProps={{ className: "mobile-tab mobile-tab--active" }}
                onClick={() => setMenuOpen(false)}
              >
                <Icon aria-hidden="true" size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </WorkspaceProvider>
  );
}
